import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { updateProfile } from '../../services/userService';
import { getAssignedProjects } from '../../services/projectService';
import { 
  formatPhoneForDisplay, 
  formatPhoneForStorage, 
  validatePhoneNumber, 
  handlePhoneKeyPress 
} from '../../utils/phoneUtils';

import { emit } from '../../hooks/useEventBus';

const MyProfile = ({ open, onClose }) => {
  const { user, setUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    workPhone: '',
    homePhone: '',
    mobilePhone: '',
    emergencyContact: '',
    emergencyPhone1: '',
    emergencyPhone2: '',
    currentPassword: '',
    newPassword: '',
    reenterPassword: '',
    defaultProject: '',
  });
  const [originalFormData, setOriginalFormData] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  // Compute which fields have been modified
  const modifiedFields = useMemo(() => {
    const modified = {};
    Object.keys(formData).forEach(key => {
      // Don't track password fields as modified since they start empty
      if (key === 'currentPassword' || key === 'newPassword' || key === 'reenterPassword') {
        modified[key] = false;
      } else {
        modified[key] = formData[key] !== (originalFormData[key] || '');
      }
    });
    return modified;
  }, [formData, originalFormData]);

  // Check if there are any unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return Object.values(modifiedFields).some(isModified => isModified) ||
           formData.currentPassword || formData.newPassword || formData.reenterPassword;
  }, [modifiedFields, formData.currentPassword, formData.newPassword, formData.reenterPassword]);

  // Check if contact info has been modified
  const hasContactChanges = useMemo(() => {
    const contactFields = ['workPhone', 'homePhone', 'mobilePhone', 'emergencyContact', 'emergencyPhone1', 'emergencyPhone2'];
    return contactFields.some(field => modifiedFields[field]);
  }, [modifiedFields]);

  // Check if password fields have content
  const hasPasswordChanges = useMemo(() => {
    return formData.currentPassword || formData.newPassword || formData.reenterPassword;
  }, [formData.currentPassword, formData.newPassword, formData.reenterPassword]);

  // Check if default project has been modified
  const hasProjectChanges = useMemo(() => {
    return modifiedFields.defaultProject;
  }, [modifiedFields]);

  useEffect(() => {
    if (user && open) {
      const initialData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        title: user.title || '',
        workPhone: user.workPhone || '',
        homePhone: user.homePhone || '',
        mobilePhone: user.mobilePhone || '',
        emergencyContact: user.emergencyContact || '',
        emergencyPhone1: user.emergencyPhone1 || '',
        emergencyPhone2: user.emergencyPhone2 || '',
        currentPassword: '',
        newPassword: '',
        reenterPassword: '',
        defaultProject: user.defaultProject?._id || user.defaultProject || '',
      };
      setFormData(initialData);
      setOriginalFormData(initialData);
      setError('');
      setSuccess('');
    }
    if (open) {
      loadProjects();
    }
  }, [user, open]);

  const loadProjects = async () => {
    try {
      const projectsData = await getAssignedProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects. Please try again later.');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear any previous errors when user makes changes
  };

  const handlePhoneChange = (event) => {
    const { name, value } = event.target;
    const formatted = formatPhoneForDisplay(value);
    setFormData(prev => ({
      ...prev,
      [name]: formatted
    }));
    setError('');
  };

  const validateForm = () => {
    if (formData.newPassword || formData.reenterPassword || formData.currentPassword) {
      if (!formData.currentPassword) {
        setError('Current password is required to change password');
        return false;
      }
      if (formData.newPassword !== formData.reenterPassword) {
        setError('New passwords do not match');
        return false;
      }
      if (formData.newPassword.length < 12) {
        setError('New password must be at least 12 characters long');
        return false;
      }
      const digitCount = (formData.newPassword.match(/\d/g) || []).length;
      const specialCharCount = (formData.newPassword.match(/[!@#$%^&*(),.?":{}|<>-]/g) || []).length;
      if (digitCount < 1) {
        setError('New password must contain at least 1 digit');
        return false;
      }
      if (specialCharCount < 1) {
        setError('New password must contain at least 1 special character');
        return false;
      }
    }
    return true;
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    
    try {
      const contactData = {
        workPhone: formData.workPhone,
        homePhone: formData.homePhone,
        mobilePhone: formData.mobilePhone,
        emergencyContact: formData.emergencyContact,
        emergencyPhone1: formData.emergencyPhone1,
        emergencyPhone2: formData.emergencyPhone2,
      };

      const updatedUser = await updateProfile(contactData);
      setUser(updatedUser);
      
      // Update original form data for contact fields
      Object.keys(contactData).forEach(key => {
        setOriginalFormData(prev => ({
          ...prev,
          [key]: formData[key]
        }));
      });
      
      emit('contactInfoUpdated');
    } catch (error) {
      console.error('Error updating contact info:', error);
      throw error;
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    
    if (!formData.currentPassword || !formData.newPassword || !formData.reenterPassword) {
      return; // Skip if no password data
    }

    try {
      const passwordData = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      };

      await updateProfile(passwordData);
      
      // Clear password fields and update original data
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        reenterPassword: '',
      }));
      
      setOriginalFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        reenterPassword: '',
      }));
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  };

  const handleProjectSubmit = async (event) => {
    event.preventDefault();
    
    try {
      const projectData = {
        defaultProject: formData.defaultProject,
      };

      const updatedUser = await updateProfile(projectData);
      setUser(updatedUser);
      
      // Update original form data for project field
      setOriginalFormData(prev => ({
        ...prev,
        defaultProject: formData.defaultProject
      }));
    } catch (error) {
      console.error('Error updating default project:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const promises = [];

      // Submit contact form if contact info changed
      if (hasContactChanges) {
        promises.push(handleContactSubmit({ preventDefault: () => {} }));
      }

      // Submit password form if password changed
      if (hasPasswordChanges) {
        promises.push(handlePasswordSubmit({ preventDefault: () => {} }));
      }

      // Submit project form if project changed
      if (hasProjectChanges) {
        promises.push(handleProjectSubmit({ preventDefault: () => {} }));
      }

      // Execute all updates
      await Promise.all(promises);
      
      setSuccess('Profile updated successfully!');
      
      // Close dialog after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...originalFormData });
    setError('');
    setSuccess('');
    setShowUnsavedWarning(false);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    resetForm();
    setShowUnsavedWarning(false);
    onClose();
  };

  const handleDialogClose = (event, reason) => {
    // Prevent closing via backdrop or escape if there are unsaved changes
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (hasUnsavedChanges) {
        setShowUnsavedWarning(true);
        return;
      }
    }
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleDialogClose} 
        maxWidth="sm" 
        fullWidth
        sx={{}}
      >
        <DialogTitle>
          My Profile - {user?.firstName} {user?.lastName}
        </DialogTitle>
        <DialogContent>
          {hasUnsavedChanges && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You have unsaved changes in your profile. Click Save to keep your changes or Cancel to discard them.
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            {/* Contact Information Form */}
            <form id="contact-form" onSubmit={handleContactSubmit} data-form-type="contact-update">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6">Contact Info</Typography>
                {hasContactChanges && (
                  <Chip 
                    label="Modified" 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    label="Work Phone"
                    name="workPhone"
                    type="tel"
                    value={formData.workPhone}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyPress}
                    fullWidth
                    disabled={loading}
                    autoComplete="off"
                    inputMode="tel"
                    pattern="[0-9-]+"
                    form="contact-form"
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                  />
                  <TextField
                    label="Home Phone"
                    name="homePhone"
                    type="tel"
                    value={formData.homePhone}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyPress}
                    fullWidth
                    disabled={loading}
                    autoComplete="off"
                    inputMode="tel"
                    pattern="[0-9-]+"
                    form="contact-form"
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                  />
                  <TextField
                    label="Mobile Phone"
                    name="mobilePhone"
                    type="tel"
                    value={formData.mobilePhone}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyPress}
                    fullWidth
                    disabled={loading}
                    autoComplete="off"
                    inputMode="tel"
                    pattern="[0-9-]+"
                    form="contact-form"
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                  />
                </Box>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <TextField
                    label="Emergency Contact"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleChange}
                    fullWidth
                    disabled={loading}
                    autoComplete="off"
                    form="contact-form"
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                  />
                  <TextField
                    label="Emergency Phone 1"
                    name="emergencyPhone1"
                    type="tel"
                    value={formData.emergencyPhone1}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyPress}
                    fullWidth
                    disabled={loading}
                    autoComplete="off"
                    inputMode="tel"
                    pattern="[0-9-]+"
                    form="contact-form"
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                  />
                  <TextField
                    label="Emergency Phone 2"
                    name="emergencyPhone2"
                    type="tel"
                    value={formData.emergencyPhone2}
                    onChange={handlePhoneChange}
                    onKeyDown={handlePhoneKeyPress}
                    fullWidth
                    disabled={loading}
                    autoComplete="off"
                    inputMode="tel"
                    pattern="[0-9-]+"
                    form="contact-form"
                    inputProps={{
                      'data-lpignore': 'true',
                      'data-1p-ignore': 'true',
                      'data-form-type': 'other'
                    }}
                  />
                </Box>
              </Box>
            </form>

            <Divider sx={{ my: 3 }} />

            {/* Password Change Form */}
            <form id="password-form" onSubmit={handlePasswordSubmit} autoComplete="off" data-form-type="password-change">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Change Password</Typography>
                {hasPasswordChanges && (
                  <Chip 
                    label="Modified" 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 2 }}>
                New passwords must contain a total of at least twelve characters with at least 1 digit and 1 special character.
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
                <TextField
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                  autoComplete="new-password"
                  form="password-form"
                  inputProps={{
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'data-form-type': 'password-change',
                    'autocomplete': 'new-password'
                  }}
                />
                <TextField
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                  autoComplete="new-password"
                  form="password-form"
                  inputProps={{
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'data-form-type': 'password-change'
                  }}
                />
                <TextField
                  label="Re-enter Password"
                  name="reenterPassword"
                  type="password"
                  value={formData.reenterPassword}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                  autoComplete="new-password"
                  form="password-form"
                  inputProps={{
                    'data-lpignore': 'true',
                    'data-1p-ignore': 'true',
                    'data-form-type': 'password-change'
                  }}
                />
              </Box>
            </form>

            <Divider sx={{ my: 3 }} />

            {/* Default Project Form */}
            <form id="project-form" onSubmit={handleProjectSubmit} data-form-type="project-update">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6">Set Default Project</Typography>
                {hasProjectChanges && (
                  <Chip 
                    label="Modified" 
                    color="primary" 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 2 }}>
                Select a default project for your timecard.
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Projects</InputLabel>
                <Select
                  name="defaultProject"
                  value={formData.defaultProject}
                  onChange={handleChange}
                  label="Projects"
                  disabled={loading}
                  form="project-form"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </form>
          </Box>
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={loading || !hasUnsavedChanges}
            color="primary"
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved Changes Warning Dialog */}
      <Dialog 
        open={showUnsavedWarning} 
        onClose={() => setShowUnsavedWarning(false)}
        sx={{}}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to discard them?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUnsavedWarning(false)}>Keep Editing</Button>
          <Button onClick={handleConfirmDiscard} color="primary" variant="contained">
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyProfile;
